/**
 * TailorInquiryModal — 定制游产品客户留咨弹窗组件（单例模式）
 *
 * 设计要点（与 dd-group-inquiry-modal 保持同一套约定）
 *   1. 单例：snippet 可以在页面任意位置被 render 任意多次，每个 <script src> 浏览器
 *      都会各自执行一遍（同名 src 不跨标签去重）。这里用 window 守卫让第二次及以后
 *      的执行整体 no-op；弹窗 DOM 只由本模块创建，永远只有一个。
 *   2. 懒挂载 + 关闭即释放：第一次点击入口才 build DOM，close() 时整体销毁
 *      （移除节点、解绑 document 监听、恢复 body 滚动）。表单值另存在 JS 里，
 *      重开时回填，所以销毁 DOM 不会丢用户已填内容。
 *   3. 回退页面释放：pagehide（含 bfcache 场景）触发 release()。
 *   4. 入口解耦：document 上一次性委托 [data-tim-trigger]，新增入口无需再绑事件。
 *
 * 视觉与交互令牌见 assets/dd-tailor-inquiry-modal.css（--dd-* 系列）
 */

;(function () {
  'use strict'

  // 单例守卫：重复执行会让 document 上的入口委托指向新闭包，
  // 而旧实例的弹窗节点仍在页面上，表现为「点开是空的」。
  if (window.TailorInquiryModal) return

  /* ======================================================================
     常量与数据
     ====================================================================== */

  var POPUP_ID = 'ddTailorInquiryModal'

  var CHECK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2"' +
      ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M20 6L9 17l-5-5"></path></svg>'

  var SPARK_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"' +
      ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"></path></svg>'

  var GLOBE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"' +
      ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="9"></circle>' +
      '<path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18"></path></svg>'

  var DEFAULT_LABELS = {
    eyebrow: 'Tailor-made journey',
    codeLabel: 'Tour Code',
    close: 'Close',

    lead: 'Tell us a little about yourself and your travel designer will build a private itinerary around you.',
    perk1: 'A dedicated China travel designer',
    perk2: 'Personalised plan with no obligation',
    perk3: 'Reply within 24 hours',
    expertName: 'DianDa Travel · China Specialists',
    expertMeta: 'Private tours, crafted locally',

    heading: 'Plan Your Trip',
    subheading: 'A few details and we take it from there. Fields marked * are required.',

    name: 'Full Name',
    namePlaceholder: 'Jane Doe',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone Number',
    phoneHint: "(We'll only call if you ask.)",
    phonePlaceholder: '415 555 0132',
    phoneCode: 'Country code',

    message: 'Your Travel Plans / Questions',
    messagePlaceholder:
      'Travel dates, group size, must-see places, special occasions… Tell us what would make this trip yours.',

    note: 'Free and no obligation · We reply within 24 hours',
    submit: 'Send My Inquiry',
    submitting: 'Sending…',

    successTitle: 'Thank you!',
    successText: 'Your inquiry has been sent. Your travel designer will get back to you within 24 hours.',
    successClose: 'Close',

    errRequired: 'This field is required.',
    errEmail: 'Please enter a valid email address.',
    errPhone: 'Please enter a valid phone number.',
    errNoApi: 'Inquiry service is not configured yet. Please email us instead.',
    errNetwork: 'Submission failed. Please try again, or email us directly.',
  }

  /* ======================================================================
     工具
     ====================================================================== */

  function esc(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)
  }

  function onlyDigits(v) {
    return String(v || '').replace(/\D/g, '')
  }

  function normalizeConfig(raw) {
    raw = raw || {}
    var labels = {}
    var key
    for (key in DEFAULT_LABELS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_LABELS, key)) labels[key] = DEFAULT_LABELS[key]
    }
    if (raw.labels) {
      for (key in raw.labels) {
        if (Object.prototype.hasOwnProperty.call(raw.labels, key) && raw.labels[key]) labels[key] = raw.labels[key]
      }
    }
    return {
      productName: raw.productName || '',
      productCode: raw.productCode || '',
      apiUrl: raw.apiUrl || '',
      labels: labels,
    }
  }

  /* ======================================================================
     状态
     ====================================================================== */

  var cfg = normalizeConfig(null)
  var elPopup = null
  var refs = {}
  var values = {}          // 表单值（销毁 DOM 后仍保留）
  var valKeyMap = {}       // data-tim-key -> 控件
  var lastFocused = null
  var prevBodyOverflow = ''
  var submitting = false

  /* ======================================================================
     Markup
     ====================================================================== */

  /** 区号表由 assets/dd-country-codes.js 统一维护，两个留咨弹窗共用 */
  function countryOptionsHtml() {
    // 现取而不是启动时缓存成变量：即便脚本顺序被调整，也不会永久拿到空表。
    // 表项格式：[区号, ISO 二字代码, 国家名称]，已按名称 A→Z 排好
    var list = window.DDCountryCodes || []
    if (!list.length) {
      console.error('[TailorInquiryModal] 未加载 assets/dd-country-codes.js，区号下拉为空。')
    }
    // 表按 A→Z 排，United States 不在首位；沿用旧行为，把美国显式标成默认项。
    // option 的 value 用 ISO 二字代码而不是区号：+1 有 25 个国家/地区，用区号做
    // value 时「重开时回填」只能命中同值的第一个（Canada 会变成 American Samoa）。
    // 选项文案是「ISO 代码 (+区号)」，例如 CN (+86)。
    var html = ''
    for (var i = 0; i < list.length; i++) {
      var c = list[i]
      html += '<option value="' + esc(c[1]) + '"' + (c[1] === 'US' ? ' selected' : '') +
        ' data-dial="' + esc(c[0]) + '">' + esc(c[1] + ' (' + c[0] + ')') + '</option>'
    }
    return html
  }

  function perkHtml(text) {
    return '<li class="tim-perk">' +
      '<span class="tim-perk__check">' + CHECK_ICON + '</span>' +
      '<span>' + esc(text) + '</span>' +
    '</li>'
  }

  function fieldsHtml(L) {
    return '' +
      '<div class="tim-field">' +
        '<label class="tim-label" for="timName">' + esc(L.name) +
          '<span class="tim-req" aria-hidden="true">*</span></label>' +
        '<input class="tim-input" id="timName" name="name" type="text" required' +
          ' autocomplete="name" placeholder="' + esc(L.namePlaceholder) + '"' +
          ' data-tim-key="name" aria-describedby="timNameErr">' +
        '<p class="tim-error" id="timNameErr" role="alert"></p>' +
      '</div>' +

      '<div class="tim-field">' +
        '<label class="tim-label" for="timEmail">' + esc(L.email) +
          '<span class="tim-req" aria-hidden="true">*</span></label>' +
        '<input class="tim-input" id="timEmail" name="email" type="email" required' +
          ' autocomplete="email" placeholder="' + esc(L.emailPlaceholder) + '"' +
          ' data-tim-key="email" aria-describedby="timEmailErr">' +
        '<p class="tim-error" id="timEmailErr" role="alert"></p>' +
      '</div>' +

      '<div class="tim-field tim-field--full">' +
        '<label class="tim-label" for="timPhone">' + esc(L.phone) +
          '<span class="tim-req" aria-hidden="true">*</span>' +
          '<span class="tim-hint">' + esc(L.phoneHint) + '</span></label>' +
        '<div class="tim-phone">' +
          '<select class="tim-select" id="timPhoneCode" name="country_code"' +
            ' aria-label="' + esc(L.phoneCode) + '" data-tim-key="phoneCode">' +
            countryOptionsHtml() +
          '</select>' +
          '<input class="tim-input" id="timPhone" name="phone" type="tel" required' +
            ' inputmode="numeric" autocomplete="tel-national" placeholder="' + esc(L.phonePlaceholder) + '"' +
            ' data-tim-key="phone" aria-describedby="timPhoneErr">' +
        '</div>' +
        '<p class="tim-error" id="timPhoneErr" role="alert"></p>' +
      '</div>' +

      '<div class="tim-field tim-field--full">' +
        '<label class="tim-label" for="timMessage">' + esc(L.message) + '</label>' +
        '<textarea class="tim-textarea" id="timMessage" name="message" rows="3"' +
          ' placeholder="' + esc(L.messagePlaceholder) + '" data-tim-key="message"></textarea>' +
      '</div>'
  }

  function buildMarkup() {
    var L = cfg.labels
    var product = cfg.productName || 'Tailor-Made Journey'
    var code = cfg.productCode || '—'
    return '' +
      '<div class="tim-backdrop" data-tim-close></div>' +
      '<div class="tim-dialog" tabindex="-1">' +
        '<aside class="tim-aside">' +
          '<span class="tim-eyebrow">' + SPARK_ICON + esc(L.eyebrow) + '</span>' +
          '<h2 class="tim-title" id="timTitle">' + esc(product) + '</h2>' +
          '<span class="tim-code">' + esc(L.codeLabel) +
            '<strong>' + esc(code) + '</strong></span>' +
          '<p class="tim-lead">' + esc(L.lead) + '</p>' +
          '<ul class="tim-perks">' +
            perkHtml(L.perk1) + perkHtml(L.perk2) + perkHtml(L.perk3) +
          '</ul>' +
          '<div class="tim-expert">' +
            '<span class="tim-expert__badge">' + GLOBE_ICON + '</span>' +
            '<span class="tim-expert__text">' +
              '<span class="tim-expert__name">' + esc(L.expertName) + '</span>' +
              '<span class="tim-expert__meta">' + esc(L.expertMeta) + '</span>' +
            '</span>' +
          '</div>' +
        '</aside>' +

        '<div class="tim-main">' +
          '<div class="tim-main__head">' +
            '<div>' +
              '<h3 class="tim-heading">' + esc(L.heading) + '</h3>' +
              '<p class="tim-subheading">' + esc(L.subheading) + '</p>' +
            '</div>' +
            '<button type="button" class="tim-close" data-tim-close' +
              ' aria-label="' + esc(L.close) + '">&times;</button>' +
          '</div>' +

          '<form class="tim-form" id="timForm" novalidate>' +
            '<div class="tim-body">' +
              '<div class="tim-grid">' + fieldsHtml(L) + '</div>' +
            '</div>' +
            '<div class="tim-status" id="timStatus" role="status" aria-live="polite"></div>' +
            '<div class="tim-footer">' +
              '<p class="tim-note">' + esc(L.note) + '</p>' +
              '<button type="submit" class="tim-submit" id="timSubmit">' + esc(L.submit) + '</button>' +
            '</div>' +
          '</form>' +

          '<div class="tim-success" id="timSuccess" hidden>' +
            '<span class="tim-success__icon">' + CHECK_ICON + '</span>' +
            '<h3 class="tim-success__title">' + esc(L.successTitle) + '</h3>' +
            '<p class="tim-success__text">' + esc(L.successText) + '<br>' +
              '<strong>' + esc(product) + '</strong></p>' +
            '<button type="button" class="tim-submit" data-tim-close>' + esc(L.successClose) + '</button>' +
          '</div>' +
        '</div>' +
      '</div>'
  }

  /* ======================================================================
     表单值：读写与跨开关保留
     ====================================================================== */

  function cacheRefs() {
    refs = {
      dialog: elPopup.querySelector('.tim-dialog'),
      form: document.getElementById('timForm'),
      status: document.getElementById('timStatus'),
      submit: document.getElementById('timSubmit'),
      success: document.getElementById('timSuccess'),
      name: document.getElementById('timName'),
      nameErr: document.getElementById('timNameErr'),
      email: document.getElementById('timEmail'),
      emailErr: document.getElementById('timEmailErr'),
      phoneCode: document.getElementById('timPhoneCode'),
      phone: document.getElementById('timPhone'),
      phoneErr: document.getElementById('timPhoneErr'),
      message: document.getElementById('timMessage'),
    }
    valKeyMap = {}
    var nodes = elPopup.querySelectorAll('[data-tim-key]')
    for (var i = 0; i < nodes.length; i++) {
      valKeyMap[nodes[i].getAttribute('data-tim-key')] = nodes[i]
    }
  }

  function applyValues() {
    for (var key in values) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) continue
      var el = valKeyMap[key]
      if (!el) continue
      el.value = values[key]
    }
  }

  function readForm() {
    for (var key in valKeyMap) {
      if (!Object.prototype.hasOwnProperty.call(valKeyMap, key)) continue
      values[key] = valKeyMap[key].value
    }
  }

  function onFormInput(e) {
    var el = e.target
    if (!el || !el.getAttribute) return
    var key = el.getAttribute('data-tim-key')
    if (!key) return
    values[key] = el.value
    // 一旦用户开始修正，立即收掉该字段的错误提示
    clearFieldError(el)
  }

  /* ======================================================================
     校验
     ====================================================================== */

  function clearFieldError(el) {
    el.removeAttribute('aria-invalid')
    var errId = el.getAttribute('aria-describedby')
    if (!errId) return
    var err = document.getElementById(errId)
    if (err) {
      err.textContent = ''
      err.classList.remove('is-show')
    }
  }

  function setFieldError(el, errEl, msg) {
    if (!errEl) return
    el.setAttribute('aria-invalid', 'true')
    errEl.textContent = msg
    errEl.classList.add('is-show')
  }

  function validate() {
    var L = cfg.labels
    var firstBad = null

    var name = refs.name.value.trim()
    if (!name) { setFieldError(refs.name, refs.nameErr, L.errRequired); firstBad = firstBad || refs.name }
    else clearFieldError(refs.name)

    var email = refs.email.value.trim()
    if (!email) { setFieldError(refs.email, refs.emailErr, L.errRequired); firstBad = firstBad || refs.email }
    else if (!isEmail(email)) { setFieldError(refs.email, refs.emailErr, L.errEmail); firstBad = firstBad || refs.email }
    else clearFieldError(refs.email)

    // 电话按「阿拉伯数字」输入，这里以位数判断有效性（含国际区号时最长 15 位）
    var phone = onlyDigits(refs.phone.value)
    if (!phone) { setFieldError(refs.phone, refs.phoneErr, L.errRequired); firstBad = firstBad || refs.phone }
    else if (phone.length < 5 || phone.length > 15) {
      setFieldError(refs.phone, refs.phoneErr, L.errPhone)
      firstBad = firstBad || refs.phone
    } else clearFieldError(refs.phone)

    if (firstBad) firstBad.focus()
    return !firstBad
  }

  /* ======================================================================
     提交
     ====================================================================== */

  /**
   * 组装提交体。字段名严格对齐 prompt/定制游API接入.md 的接口约定：
   * snake_case 命名、区号(country_code)与号码(phone)分开、产品信息由入口配置带入。
   * nationality 在定制游页面规格里没有对应输入项（见表单字段约定），但接口示例
   * 包含该键，这里补空串保持提交体结构一致，后端可按选填处理。
   */
  function collect() {
    return {
      name: refs.name.value.trim(),
      nationality: '',
      email: refs.email.value.trim(),
      country_code: selectedDialCode(),
      phone: onlyDigits(refs.phone.value),
      product_name: cfg.productName || '',
      product_code: cfg.productCode || '',
      message: refs.message.value.trim(),
    }
  }

  /** option 的 value 是 ISO 二字代码（区号会重复），提交时从 data-dial 取回区号 */
  function selectedDialCode() {
    if (!refs.phoneCode) return ''
    var opt = refs.phoneCode.options[refs.phoneCode.selectedIndex]
    return (opt && opt.getAttribute('data-dial')) || ''
  }

  function showStatus(kind, msg) {
    if (!refs.status) return
    refs.status.className = 'tim-status is-show is-' + kind
    refs.status.textContent = msg
  }

  function clearStatus() {
    if (!refs.status) return
    refs.status.className = 'tim-status'
    refs.status.textContent = ''
  }

  function setSubmitting(on) {
    submitting = on
    if (!refs.submit) return
    refs.submit.disabled = on
    refs.submit.innerHTML = on
      ? '<span class="tim-spinner" aria-hidden="true"></span>' + esc(cfg.labels.submitting)
      : esc(cfg.labels.submit)
  }

  function showSuccess() {
    if (!refs.form || !refs.success) return
    // 提交成功后清空表单，否则 close() 里的 readForm 会把已提交的内容当成
    // 「未提交的草稿」存回 values，重开时又原样回填
    refs.form.reset()
    values = {}
    refs.form.hidden = true
    refs.success.hidden = false
    var btn = refs.success.querySelector('.tim-submit')
    if (btn) btn.focus()
  }

  function showForm() {
    if (!refs.form || !refs.success) return
    refs.success.hidden = true
    refs.form.hidden = false
  }

  function onSubmit(e) {
    e.preventDefault()
    if (submitting) return
    if (!validate()) return

    if (!cfg.apiUrl) {
      // 静默失败会让用户以为按钮坏了，这里必须给出可见反馈
      console.error('[TailorInquiryModal] 未配置 api_url，表单无法提交。' +
        '请在 section 设置或 snippet 参数里传入 api_url。')
      showStatus('error', cfg.labels.errNoApi)
      return
    }

    clearStatus()
    setSubmitting(true)

    fetch(cfg.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(collect()),
    })
      .then(function (res) {
        // 接口响应体是 {code, msg}，校验失败时还带 errors 数组。
        // 转达服务端原因，比笼统的「提交失败」有用得多；跨域被拦时 msg 也会
        // 直接说明原因（"来源域名未被允许" = 当前域名不在白名单）。
        return res.text().then(function (text) {
          var data = null
          try { data = JSON.parse(text) } catch (_) {}
          var code = data && data.code != null ? Number(data.code) : null
          if (res.ok && (code === null || code < 400)) return
          var detail = data && data.errors && data.errors.length
            ? data.errors.join(' / ')
            : ((data && data.msg) || ('HTTP ' + res.status))
          var e = new Error(detail)
          e.serverMsg = detail
          throw e
        })
      })
      .then(function () {
        // 弹窗可能在请求期间被关掉（组件已销毁），此时不再动 DOM
        if (!elPopup) return
        setSubmitting(false)
        showSuccess()
      })
      .catch(function (err) {
        if (!elPopup) return
        setSubmitting(false)
        showStatus('error', (err && err.serverMsg) || cfg.labels.errNetwork)
        console.error('[TailorInquiryModal] 提交失败：', err)
      })
  }

  function onPhoneInput(e) {
    var input = e.target
    var digits = onlyDigits(input.value)
    if (input.value !== digits) input.value = digits
    values.phone = digits
    clearFieldError(input)
  }

  /* ======================================================================
     挂载 / 销毁
     ====================================================================== */

  function onPopupClick(e) {
    if (e.target.closest('[data-tim-close]')) {
      e.preventDefault()
      close()
    }
  }

  function onKeydown(e) {
    if (!elPopup) return
    if (e.key === 'Escape') {
      close()
      return
    }
    if (e.key !== 'Tab') return
    trapFocus(e)
  }

  function focusables() {
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
              'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    var out = []
    var nodes = elPopup.querySelectorAll(sel)
    for (var i = 0; i < nodes.length; i++) {
      // 用 getClientRects 而不是 offsetParent：后者对 position:fixed 恒为 null
      if (nodes[i].getClientRects().length) out.push(nodes[i])
    }
    return out
  }

  function trapFocus(e) {
    var list = focusables()
    if (!list.length) return
    var first = list[0]
    var last = list[list.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  function mount() {
    if (elPopup) return
    elPopup = document.createElement('div')
    elPopup.id = POPUP_ID
    elPopup.className = 'tim-popup'
    elPopup.setAttribute('role', 'dialog')
    elPopup.setAttribute('aria-modal', 'true')
    elPopup.setAttribute('aria-labelledby', 'timTitle')
    elPopup.innerHTML = buildMarkup()
    document.body.appendChild(elPopup)

    cacheRefs()
    applyValues()

    elPopup.addEventListener('click', onPopupClick)
    refs.form.addEventListener('submit', onSubmit)
    refs.form.addEventListener('input', onFormInput)
    refs.form.addEventListener('change', onFormInput)
    refs.phone.addEventListener('input', onPhoneInput)
    document.addEventListener('keydown', onKeydown)

    prevBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  /** 释放资源：移除节点、解绑监听、恢复页面滚动。表单值保留在内存里供重开回填。 */
  function release() {
    if (!elPopup) return
    readForm()

    elPopup.removeEventListener('click', onPopupClick)
    if (refs.form) {
      refs.form.removeEventListener('submit', onSubmit)
      refs.form.removeEventListener('input', onFormInput)
      refs.form.removeEventListener('change', onFormInput)
    }
    if (refs.phone) refs.phone.removeEventListener('input', onPhoneInput)
    document.removeEventListener('keydown', onKeydown)

    elPopup.remove()
    elPopup = null
    refs = {}
    valKeyMap = {}
    submitting = false
    document.body.style.overflow = prevBodyOverflow
    prevBodyOverflow = ''
  }

  /* ======================================================================
     对外行为
     ====================================================================== */

  /** 标题 / 产品编号 / 接口变了，就必须重建 DOM，否则会挂着上一个产品的信息。 */
  function sameConfig(a, b) {
    return a.productName === b.productName &&
           a.productCode === b.productCode &&
           a.apiUrl === b.apiUrl
  }

  function open(config) {
    var next = config ? normalizeConfig(config) : cfg
    // 同一页可能存在多个不同产品的入口（如列表页每张卡片一个）。若弹窗还开着就被
    // 切到另一个产品，必须重建 —— mount() 对已存在的节点是直接返回的。
    // release() 会先把当前表单值读进 values，重建后由 applyValues() 回填，草稿不丢。
    if (elPopup && !sameConfig(cfg, next)) release()
    cfg = next

    mount()
    if (!elPopup) return

    showForm()
    clearStatus()
    setSubmitting(false)

    lastFocused = document.activeElement
    elPopup.classList.add('is-open')

    // 打开后落到第一个还没填的必填项，直接可以开始打字。
    // 草稿已填满时退回 dialog 本身，避免焦点留在弹窗外。
    var target = null
    if (!refs.name.value.trim()) target = refs.name
    else if (!refs.email.value.trim()) target = refs.email
    else if (!refs.phone.value) target = refs.phone
    else target = refs.dialog
    if (target) {
      try { target.focus({ preventScroll: true }) } catch (_) { target.focus() }
    }
  }

  function close() {
    if (!elPopup) return
    release()
    // 触发入口可能已被页面重绘掉，先确认还在文档里再还焦点
    if (lastFocused && lastFocused.focus && document.contains(lastFocused)) {
      try { lastFocused.focus({ preventScroll: true }) } catch (_) { lastFocused.focus() }
    }
    lastFocused = null
  }

  /** 只记录默认配置，不建 DOM —— 真正的节点在第一次 open 时才创建。 */
  function init(config) {
    if (config) cfg = normalizeConfig(config)
  }

  /* ======================================================================
     入口委托：整页只绑一次，之后新增多少入口都自动生效
     ====================================================================== */

  function findConfig(code) {
    var list = window.__timConfigs || []
    for (var i = 0; i < list.length; i++) {
      if (code && String(list[i].productCode) === String(code)) return list[i]
    }
    return null
  }

  function onDocClick(e) {
    var target = e.target
    if (!target || !target.closest) return
    var btn = target.closest('[data-tim-trigger]')
    if (!btn) return
    e.preventDefault()
    var code = btn.getAttribute('data-tim-code') || ''
    open(findConfig(code) || window.__timInitConfig || {})
  }

  document.addEventListener('click', onDocClick)

  // 回退 / 关闭页面即释放弹窗资源（pagehide 同时覆盖 bfcache 进出）
  window.addEventListener('pagehide', function () {
    if (elPopup) release()
  })

  window.TailorInquiryModal = {
    init: init,
    open: open,
    close: close,
    release: release,
    isOpen: function () { return !!(elPopup && elPopup.classList.contains('is-open')) },
    getConfig: function () { return cfg },
  }

  // Liquid 注入的默认配置：有就记下来，但仍然等点击才建 DOM
  if (window.__timInitConfig) {
    cfg = normalizeConfig(window.__timInitConfig)
  }
})()
